import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, MapPin, Phone, CheckCircle2, Clock, ChevronRight } from 'lucide-react';
import { usePageMeta, useFAQSchema } from '../../hooks/useSEO';
import BreadcrumbSchema from '../../components/seo/BreadcrumbSchema';
import { NZLeadFunnelModal } from './components/NZLeadFunnelModal';

const BREADCRUMB = [
  { name: 'Home', url: '/' },
  { name: 'Study Abroad', url: '/study-abroad/' },
  { name: 'New Zealand', url: '/study-abroad/newzealand/' },
];

const FAQS = [
  { q: 'How much bank balance is required for a New Zealand student visa from India in 2026?', a: 'As per Immigration New Zealand (INZ) rules for 2026, tertiary students must show at least NZD 20,000 per year for living expenses, in addition to first-year tuition fees, accessible via approved liquid funds or the Fund Transfer Scheme (FTS).' },
  { q: 'Can I work while studying in New Zealand as an Indian student?', a: 'Yes, eligible full-time tertiary students can work part-time up to 25 hours per week during term time and full-time during official scheduled university breaks.' },
  { q: 'What is the New Zealand Green List and how does it help Indian students get PR?', a: 'The Green List contains specified high-demand skilled roles in NZ (Engineering, IT, Healthcare, Construction). Graduating into a Green List role provides direct or fast-tracked pathways to New Zealand Permanent Residency (PR).' },
  { q: 'Is IELTS mandatory for a New Zealand student visa?', a: 'While IELTS is widely accepted, students can also fulfill English language requirements using PTE Academic or TOEFL iBT, depending on university admission criteria.' },
  { q: 'How long is the Post-Study Work Visa (PSWV) in New Zealand?', a: 'Depending on your level of qualification (Bachelor’s, Master’s, or PhD) and course duration, a Post-Study Work Visa is granted for up to 3 years.' },
  { q: 'Why choose ZAFCO Study Abroad as your New Zealand consultant in Delhi NCR?', a: 'ZAFCO combines MEA-approved global mobility expertise with dedicated education counselling, providing end-to-end support for university shortlisting, NZD fund verification, visa lodging, and post-graduation career placement.' },
];

const SUBNAV = [
  ['#overview', 'Overview'], ['#costs-funds', 'Costs & Funds'], ['#courses-unis', 'Courses & Unis'],
  ['#visa-process', 'Visa Process'], ['#greenlist-pr', 'Green List PR'], ['#faqs', 'FAQs'],
];

const TAKEAWAYS = [
  'All 8 public universities ranked in the global QS World Top 500 (QS 2026), led by University of Auckland at #65.',
  'Show at least NZD 20,000/year in living-cost funds (NZD 1,667/month if under a year), separate from tuition.',
  'Work up to 25 hours/week during term time (up from 20) since Immigration NZ’s 3 November 2025 rule change.',
  'New Short-Term Graduate Work Visa (6 months open work rights) opens 16 November 2026, plus expanded PSWV eligibility.',
  'NZ Excellence Awards (NZEA): NZD 260,000 pool across 29 scholarships (NZD 5,000–20,000 each) exclusively for Indian students.',
  'Ranked 2nd most peaceful country in the world (2026 Global Peace Index) — highest in Asia-Pacific.',
  'Visa approval requires "genuine intentions to study": enrolment evidence, fee-payment ability, and credible post-study plans.',
];

const COMPARE = [
  { flag: '🇳🇿', c: 'New Zealand', tuition: 'NZD 20,000–40,000/yr (₹11–22 L)', living: 'NZD 20,000/yr (₹11 L)', psw: 'Up to 3 years PSWV + new 6-month Short-Term Graduate Visa (from 16 Nov 2026)', pr: 'Centrally defined Green List: Tier 1 straight-to-residence; Tier 2 after 24 months', hi: true },
  { flag: '🇦🇺', c: 'Australia', tuition: 'AUD 25,000–45,000/yr (₹17–30 L)', living: 'AUD 29,710/yr (₹20 L)', psw: 'Post-study work rights; higher costs in Sydney/Melbourne', pr: 'Points-based skilled migration; larger job market, less defined fast-tracks' },
  { flag: '🇨🇦', c: 'Canada', tuition: 'CAD ~24,000–42,000/yr (₹16–29 L)', living: 'CAD 22,895/yr (₹16 L)', psw: 'PGWP; wider institution quality range', pr: 'Provincial/federal programmes; colder winters' },
];

const TIMELINE = [
  ['Receive Offer of Place', 'Secure an offer/enrolment confirmation from an approved provider.'],
  ['Pay Tuition Fees', 'Pay the required first-course/first-year fee and obtain confirmation.'],
  ['Prepare Proof of Funds', 'NZD 20,000/year (or NZD 1,667/month for shorter courses) via savings, FTS, loan, or sponsor income.'],
  ['Gather Supporting Evidence', 'Genuine Student documentation, medical certificate, police clearance where required.'],
  ['Submit Online Application', 'Lodge via Immigration New Zealand’s online portal with visa fee and levy.'],
  ['Decision & Travel', 'Processing times vary by season — check the live INZ processing-time page before applying.'],
];

const UNIS = [
  ['University of Auckland', '65'], ['University of Otago', '197'], ['Massey University', '230'],
  ['Victoria University of Wellington', '240'], ['University of Canterbury', '261'], ['University of Waikato', '281'],
  ['Lincoln University', '407'], ['Auckland University of Technology (AUT)', '410'],
];

const GREENLIST = [
  ['Construction & Engineering', 'Civil, mechanical, electrical, industrial engineers; construction project managers — many roles in Tier 1 "Straight to Residence".'],
  ['IT, Data & Cybersecurity', 'ICT and communications engineering professionals mapped across Green List tiers.'],
  ['Health, Nursing & Aged Care', 'Multiple caring and health professions with fast-tracked residence pathways.'],
  ['Agritech / Agribusiness', 'Dairy farming, agricultural science, agribusiness management — Lincoln & Massey strongholds.'],
  ['Trades (Tier 2, added Aug 2025)', 'Metal fabricator, fitter, welder, panel beater — residence after 24 months of qualifying work.'],
  ['Hospitality & Tourism', 'Strong regional employment relevance; Ara Institute and university tourism majors.'],
];

const EDU_ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'EducationalOrganization',
  name: 'ZAFCO Study Abroad',
  url: 'https://zafco.in/study-abroad/newzealand/',
  logo: 'https://zafco.in/assets/logo.png',
  telephone: '+91-9990533327',
  sameAs: ['https://wa.me/919990533327'],
  address: { '@type': 'PostalAddress', addressLocality: 'New Delhi', addressRegion: 'Delhi NCR', addressCountry: 'IN' },
  areaServed: 'IN',
  description: 'MEA-approved study abroad consultancy providing expert guidance for New Zealand university admissions, student visas, and career pathways.',
  hasOfferCatalog: {
    '@type': 'OfferCatalog', name: 'Study Abroad Services',
    itemListElement: [{ '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Free Profile Evaluation & University Shortlisting' } }],
  },
};

const H2 = ({ children }) => <h2 className="text-2xl sm:text-3xl font-bold font-syne text-[#003087] leading-tight mb-5">{children}</h2>;

const MicroTrust = ({ dark }) => <span className={`zaf-micro-trust block text-[11px] mt-2 ${dark ? 'text-slate-300' : 'text-slate-500'}`}>⚡ Takes 60 seconds • 100% Free • No Spam Guarantee</span>;

export default function NewZealandHubPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState('#overview');
  const triggerRef = useRef(null);

  usePageMeta(
    'Study in New Zealand for Indian Students 2026',
    'New Zealand student visa from India made simple: 2026 intake timeline, costs, post-study work visa & PR routes, plus MEA-approved expert support.',
    { canonical: '/study-abroad/newzealand/' },
  );
  useFAQSchema(FAQS.map((f) => ({ question: f.q, answer: f.a })));

  useEffect(() => {
    // OG tags + EducationalOrganization JSON-LD
    const metas = [
      ['property', 'og:title', 'Study in New Zealand 2026: Complete Guide for Indian Students (Visa, Costs, PR)'],
      ['property', 'og:description', 'Discover top New Zealand universities, 2026 student visa rules, NZD 20,000 fund requirements, post-study work options and PR pathways – with free counselling from Zafco Study Abroad.'],
    ];
    const created = [];
    metas.forEach(([attr, key, val]) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); created.push(el); }
      el.setAttribute('content', val);
    });
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.setAttribute('data-nz-eduorg', '1');
    ld.text = JSON.stringify(EDU_ORG_SCHEMA).replace(/</g, '\\u003c');
    document.head.appendChild(ld);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'nz_hub_page_viewed', page_type: 'hub' });
    const fired = new Set();
    const onScroll = () => {
      for (const [href] of [...SUBNAV].reverse()) {
        const el = document.querySelector(href);
        if (el && el.getBoundingClientRect().top < 140) { setActiveAnchor(href); break; }
      }
      const doc = document.documentElement;
      const pct = Math.min(100, Math.round((doc.scrollTop / (doc.scrollHeight - doc.clientHeight)) * 100));
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !fired.has(m)) { fired.add(m); window.dataLayer.push({ event: 'nz_hub_scroll_depth', depth: m, page_type: 'hub' }); }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { ld.remove(); created.forEach((el) => el.remove()); window.removeEventListener('scroll', onScroll); };
  }, []);

  const openModal = (ctaId) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'nz_cta_click', cta_id: ctaId });
    window.dataLayer.push({ event: 'nz_lead_started', lead_source: 'nz-hub' });
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); triggerRef.current?.focus(); };

  return (
    <div className="zaf-edu-platform bg-white" data-testid="nz-hub-root">
      <BreadcrumbSchema items={BREADCRUMB} />
      {/* Zone A — Hero */}
      <header className="zaf-hero bg-[#0A192F] text-white">
        <div className="container mx-auto px-6 max-w-6xl py-14 lg:py-20">
          <nav aria-label="Breadcrumb" className="mb-6" data-testid="nz-hub-breadcrumb">
            <ol className="flex flex-wrap items-center gap-1 text-xs text-slate-300">
              {BREADCRUMB.map((b, i) => (
                <li key={b.name} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="w-3 h-3" aria-hidden="true" />}
                  {i < BREADCRUMB.length - 1
                    ? <Link to={b.url} className="hover:text-[#C5A028] underline-offset-2 hover:underline">{b.name}</Link>
                    : <span className="text-[#C5A028] font-semibold" aria-current="page">{b.name}</span>}
                </li>
              ))}
            </ol>
          </nav>
          <div className="flex flex-wrap gap-2 mb-5">
            {['Post-Study Work Visa', 'Green List PR Pathway', 'Work 25 Hrs/Week', 'NZD 20K Funds Rule'].map((b) => (
              <span key={b} className="px-3 py-1 rounded-full bg-white/10 border border-[#C5A028]/50 text-[#C5A028] text-xs font-semibold">{b}</span>
            ))}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-syne leading-tight mb-6">Study in New Zealand for Indian Students 2026</h1>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mb-8 max-w-4xl">
            {TAKEAWAYS.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed">
                <CheckCircle2 className="w-4 h-4 text-[#C5A028] mt-0.5 shrink-0" aria-hidden="true" />{t}
              </li>
            ))}
          </ul>
          <button ref={triggerRef} onClick={() => openModal('nz-hero-cta')} data-testid="nz-hero-cta"
            className="bg-[#C5A028] hover:bg-[#d4b23e] text-[#0A192F] font-bold text-sm px-8 py-4 rounded-full focus-visible:ring-2 focus-visible:ring-white transition-colors">
            Check Your NZ Eligibility
          </button>
          <MicroTrust dark />
        </div>
      </header>

      {/* Zone B — Sticky Subnav */}
      <nav className="zaf-sticky-subnav sticky top-0 z-[100] bg-white/95 backdrop-blur border-b border-slate-200 overflow-x-auto" aria-label="Page sections">
        <div className="container mx-auto px-6 max-w-6xl flex gap-1 py-2 whitespace-nowrap">
          {SUBNAV.map(([href, label]) => (
            <a key={href} href={href} data-testid={`nz-subnav-${href.slice(1)}`}
              onClick={() => { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'nz_toc_click', toc: href }); }}
              className={`px-3 py-2 rounded-full text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-[#C5A028] ${activeAnchor === href ? 'bg-[#003087] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{label}</a>
          ))}
        </div>
      </nav>

      <div className="container mx-auto px-6 max-w-6xl py-12 space-y-16">
        {/* Zone C — Comparison Matrix */}
        <section id="overview" className="scroll-mt-24">
          <H2>New Zealand vs Australia vs Canada (2026)</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMPARE.map((d) => (
              <article key={d.c} className={`rounded-2xl border-2 p-6 ${d.hi ? 'border-[#C5A028] bg-amber-50/40 shadow-md' : 'border-slate-200 bg-white'}`} data-testid={`nz-compare-${d.c.toLowerCase().replace(/\s/g, '-')}`}>
                <h3 className="text-lg font-bold text-[#003087] mb-4">{d.flag} {d.c}</h3>
                {[['Tuition', d.tuition], ['Living Expenses', d.living], ['Post-Study Work', d.psw], ['PR Ease', d.pr]].map(([k, v]) => (
                  <p key={k} className="text-sm text-slate-700 mb-3 leading-relaxed"><strong className="text-[#003087]">{k}:</strong> {v}</p>
                ))}
              </article>
            ))}
          </div>
        </section>

        {/* Zone D1 — Costs & Funds */}
        <section id="costs-funds" className="scroll-mt-24">
          <H2>Cost of Studying & Bank Balance (FTS) Rules</H2>
          <div className="zaf-alert-card bg-[#0A192F] border-l-4 border-[#C5A028] rounded-r-xl p-6 mb-8" data-testid="nz-20k-alert">
            <p className="text-[#C5A028] font-bold text-lg font-syne mb-1">The NZD 20,000 Living Expense Rule</p>
            <p className="text-slate-300 text-sm leading-relaxed">Immigration New Zealand requires proof of <strong className="text-white">NZD 20,000 per year</strong> (or NZD 1,667/month for courses under a year) in living funds — <strong className="text-white">separate from tuition</strong>. Show via personal savings, family sponsorship, education loans, or the Funds Transfer Scheme (FTS), with both primary evidence of funds and secondary evidence of their source.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm text-left">
              <thead><tr className="bg-slate-100">{['Field', "Bachelor's (NZD/yr)", "Master's (NZD/yr)"].map((h) => <th key={h} className="px-4 py-3 font-bold text-[#003087]">{h}</th>)}</tr></thead>
              <tbody>
                {[['Arts, Humanities, Business', '20,000 – 32,000', '26,000 – 40,000'], ['Engineering & IT', '28,000 – 35,000', '40,000 – 65,000'], ['Health Sciences / Medicine', '35,000 – 70,000+', '35,000 – 70,000+'], ['MBBS/MD (University of Otago only)', '70,000 – 80,000', '—'], ['PhD (all fields)', 'NZD 6,500 – 9,000/yr (domestic-equivalent rate)', '—']].map((r) => (
                  <tr key={r[0]} className="border-t border-slate-100">{r.map((c, i) => <td key={i} className="px-4 py-3 text-slate-700">{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">Realistic monthly spending: NZD 1,500–3,000 — Auckland most expensive; Dunedin, Hamilton and Christchurch notably cheaper. INR figures indicative at 1 NZD ≈ ₹56 (Aug 2026 mid-market).</p>
          <p className="text-sm text-slate-700 mt-4">Need the full verified financial picture — tuition tables, FTS mechanics, hidden costs and sample budgets? Read the complete <Link to="/study-abroad/newzealand/cost-of-study-living/" className="text-[#003087] font-semibold underline" data-testid="nz-hub-cost-spoke-link">NZ Cost of Study & Living Breakdown (2026) →</Link></p>
        </section>

        {/* Courses & Unis */}
        <section id="courses-unis" className="scroll-mt-24">
          <H2>Top Universities — QS World University Rankings 2026</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {UNIS.map(([u, r]) => (
              <div key={u} className="flex items-center justify-between rounded-xl border border-slate-200 px-5 py-4">
                <span className="text-sm font-semibold text-slate-800">{u}</span>
                <span className="text-xs font-bold text-white bg-[#003087] rounded-full px-3 py-1 shrink-0 ml-3">QS #{r}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">All 8 public universities sit in the QS World Top 500 (top ~33% of 1,500+ ranked institutions). QS 2027 (released 18 Jun 2026) moves Auckland to #67.</p>
        </section>

        {/* Zone D2 — Visa Timeline */}
        <section id="visa-process" className="scroll-mt-24">
          <H2>Student Visa Process — Step by Step</H2>
          <ol className="zaf-timeline relative border-l-2 border-[#003087]/20 ml-3 space-y-8">
            {TIMELINE.map(([t, d], i) => (
              <li key={t} className="pl-8 relative">
                <span className="absolute -left-[15px] top-0 w-7 h-7 rounded-full bg-[#003087] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <h3 className="text-base font-bold text-[#003087] mb-1">{t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-5 text-sm text-slate-700">
            <Clock className="w-4 h-4 text-[#003087] mt-0.5 shrink-0" aria-hidden="true" />
            <p><strong>Residency funnel:</strong> Study → Post-Study/Short-Term Graduate Work Visa → Skilled job under AEWV → Green List (Tier 1/2) or Skilled Migrant residence → Permanent settlement. Tier 1 roles can move to residence almost immediately with a qualifying job; Tier 2 typically requires ~24 months of qualifying employment.</p>
          </div>
          <p className="text-sm text-slate-700 mt-4">Need adviser-grade depth — AIP/FTS deadlines, document checklist, funds evidence rules and refusal reasons? Read the complete <Link to="/study-abroad/newzealand/student-visa-guide/" className="text-[#003087] font-semibold underline" data-testid="nz-hub-visa-spoke-link">NZ Student Visa Guide 2026 →</Link></p>
        </section>

        {/* Zone E — Green List PR */}
        <section id="greenlist-pr" className="scroll-mt-24">
          <H2>Green List Occupations & Post-Study Work Rights</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            {GREENLIST.map(([t, d]) => (
              <article key={t} className="rounded-2xl border border-slate-200 p-6 hover:border-[#C5A028] transition-colors">
                <h3 className="text-base font-bold text-[#003087] mb-2">{t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
              </article>
            ))}
          </div>
          {/* CONTENT_MISSING: Exact graduate salary ranges per Green List role — INZ revises wage thresholds periodically; pull live from immigration.govt.nz at publish time */}
          <p className="text-xs text-slate-500 mb-6">Exact current salary bands by role are revised periodically — verified live from Immigration New Zealand’s Green List page at counselling time. ZAFCO’s global placement network maps your course to Tier 1/Tier 2 outcomes before you apply.</p>
          <p className="text-sm text-slate-700 mb-6">Want the full Tier 1 vs Tier 2 breakdown, occupation lists and residence timelines? Read the complete <Link to="/study-abroad/newzealand/green-list-pr-guide/" className="text-[#003087] font-semibold underline" data-testid="nz-hub-greenlist-spoke-link">New Zealand Green List PR Guide 2026 →</Link></p>
          <div className="bg-[#0A192F] rounded-2xl p-7 text-white">
            <h3 className="text-lg font-bold text-[#C5A028] font-syne mb-2">Scholarships: NZ Excellence Awards (NZEA)</h3>
            <p className="text-sm text-slate-300 leading-relaxed">NZD 260,000 total pool • 29 scholarships • NZD 5,000–20,000 each — exclusively for Indian citizens with an unconditional offer, jointly funded by Education New Zealand and all 8 universities.</p>
          </div>
        </section>

        {/* Zone F — Local Trust */}
        <section id="local-trust" className="scroll-mt-24">
          <H2>MEA-Approved New Zealand Consultants — Delhi NCR</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="rounded-2xl border border-slate-200 p-6">
              <ShieldCheck className="w-7 h-7 text-[#003087] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold text-[#003087] mb-1">MEA-Approved Consultancy</h3>
              <p className="text-sm text-slate-600 leading-relaxed">Govt. of India MEA licence (B-1873/GUJ/COM/100/5/10332/2023) — regulated, accountable guidance for university shortlisting, NZD fund verification and visa lodging.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6">
              <MapPin className="w-7 h-7 text-[#003087] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold text-[#003087] mb-1">Delhi NCR Support</h3>
              <p className="text-sm text-slate-600 leading-relaxed">In-person counselling in New Delhi, Delhi NCR — document checks, Genuine Student statement reviews and pre-departure briefings.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6">
              <Phone className="w-7 h-7 text-[#003087] mb-3" aria-hidden="true" />
              <h3 className="text-base font-bold text-[#003087] mb-1">Free Profile Evaluation</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">University shortlisting + intake planning at zero cost. Call <a href="tel:+919990533327" className="font-semibold text-[#003087] underline">+91-9990533327</a> or start below.</p>
              <button onClick={() => openModal('nz-trust-cta')} data-testid="nz-trust-cta" className="bg-[#003087] hover:bg-[#0056B3] text-white font-bold text-xs px-5 py-2.5 rounded-full focus-visible:ring-2 focus-visible:ring-[#C5A028]">Check Your NZ Eligibility</button>
              <MicroTrust />
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section id="faqs" className="scroll-mt-24">
          <H2>New Zealand Student Visa FAQs</H2>
          {FAQS.map((f) => (
            <details key={f.q} className="group border border-slate-200 rounded-xl mb-3 overflow-hidden">
              <summary className="cursor-pointer list-none px-5 py-4 text-sm font-bold text-[#003087] flex justify-between items-center focus-visible:ring-2 focus-visible:ring-[#C5A028]" tabIndex={0}>
                {f.q}<span className="text-[#C5A028] group-open:rotate-45 transition-transform text-lg" aria-hidden="true">+</span>
              </summary>
              <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
          <p className="text-sm text-slate-600 mt-6">Compare destinations: <Link to="/study-abroad/germany/" className="text-[#003087] font-semibold underline">Study in Germany</Link> · <Link to="/study-abroad/uk/" className="text-[#003087] font-semibold underline">Study in the UK</Link> · <Link to="/study-abroad/comparison/" className="text-[#003087] font-semibold underline">Country Comparisons</Link></p>
        </section>
      </div>

      <NZLeadFunnelModal open={modalOpen} onClose={closeModal} />
    </div>
  );
}
